import { AppId } from "../../app/appTypes"
import { RequirementNote } from "../ursOffer/ursOfferSlice"
import { Blobs } from "../documents/attachementsSlice"
import { URSComment } from "./URSCommentSlice"
import { Urs, URSRequirement, UrsRequirementType } from "./UrsSlice"

// "Unresolved" is `status === 'N'` on a top-level comment. Replies carry their own
// status but are not what the reviewer works through, so they never make a
// requirement count as unresolved on their own — same rule the tab badge and the
// skip-to-next-comment buttons already use.
export type CommentsFilter = 'all' | 'with' | 'unresolved'

export type AttachmentsFilter = 'all' | 'with'

export interface RequirementFilter {
    comments: CommentsFilter
    attachments: AttachmentsFilter
    // Empty set means "no constraint" rather than "match nothing" — that way an
    // untouched multi-select behaves like an absent filter.
    criticality: Set<string>
    status: Set<string>
    note: Set<RequirementNote>
    text: string
}

export const emptyRequirementFilter: RequirementFilter = {
    comments: 'all',
    attachments: 'all',
    criticality: new Set(),
    status: new Set(),
    note: new Set(),
    text: '',
}

export function isFilterActive(filter: RequirementFilter): boolean {
    return filter.comments !== 'all'
        || filter.attachments !== 'all'
        || filter.criticality.size > 0
        || filter.status.size > 0
        || filter.note.size > 0
        || filter.text.trim() !== ''
}

export interface CommentCounts {
    total: number
    unresolved: number
}

export type CommentIndex = Map<AppId, CommentCounts>

// Shared so callers reading a requirement the index has no entry for don't each
// allocate their own zero object on every render.
export const emptyCommentCounts: CommentCounts = { total: 0, unresolved: 0 }

// One pass over the comment list instead of the two per-row scans the table used
// to do (see URSRequirementRow) — with a few hundred requirements and comments
// that was the dominant cost of a re-render.
export function buildCommentIndex(comments: URSComment[]): CommentIndex {
    const index: CommentIndex = new Map()
    for (const c of comments) {
        if (c.parentCommentId !== null || c.ursRequirementId === null) continue
        const entry = index.get(c.ursRequirementId)
        if (entry === undefined) {
            index.set(c.ursRequirementId, { total: 1, unresolved: c.status === 'N' ? 1 : 0 })
        } else {
            entry.total += 1
            if (c.status === 'N') entry.unresolved += 1
        }
    }
    return index
}

// Requirement guids with at least one attachment. Blobs are keyed by guid while
// comments are keyed by id, so the two lookups cannot share a map.
//
// A guid missing from the blobs slice means "not loaded", which this treats the
// same as "no attachments" — sound only because loadUrs (and loadUrsTemplate)
// issue one loadManyBlobs for every requirement guid before resolving, so the
// slice is complete for this document by the time the screen renders. Make that
// load lazy and the filter silently starts hiding requirements that do have files.
export type AttachmentIndex = Set<string>

export function buildAttachmentIndex(blobs: Blobs[]): AttachmentIndex {
    const index: AttachmentIndex = new Set()
    for (const entry of blobs) {
        if (entry.blobs !== null && entry.blobs.length > 0) {
            index.add(entry.resourceId)
        }
    }
    return index
}

// Everything the pure filter needs to look up about a requirement. Bundled so a
// new facet doesn't mean threading another parameter through three functions.
export interface FilterIndex {
    comments: CommentIndex
    attachments: AttachmentIndex
}

export function buildFilterIndex(comments: URSComment[], blobs: Blobs[]): FilterIndex {
    return {
        comments: buildCommentIndex(comments),
        attachments: buildAttachmentIndex(blobs),
    }
}

// Requirement bodies are HTML, so a raw `name` match would hit tag names and
// attributes. `searcheableContent` is the backend's plain-text projection; fall
// back to a crude tag strip when it is missing (older records).
function searchableText(req: URSRequirement): string {
    if (req.searcheableContent !== null && req.searcheableContent !== undefined) {
        return req.searcheableContent
    }
    return req.name.replace(/<[^>]*>/g, ' ')
}

export function matchesFilter(req: URSRequirement, filter: RequirementFilter, index: FilterIndex): boolean {
    const counts = index.comments.get(req.id) ?? emptyCommentCounts

    if (filter.comments === 'with' && counts.total === 0) return false
    if (filter.comments === 'unresolved' && counts.unresolved === 0) return false

    if (filter.attachments === 'with' && !index.attachments.has(req.guid)) return false

    if (filter.criticality.size > 0 && !filter.criticality.has(req.criticality ?? '')) return false
    if (filter.status.size > 0 && !filter.status.has((req.status ?? '').toUpperCase())) return false
    if (filter.note.size > 0 && !filter.note.has(req.note ?? '')) return false

    const text = filter.text.trim().toLowerCase()
    if (text !== '' && !searchableText(req).toLowerCase().includes(text)) return false

    return true
}

// What the table needs to know per requirement type. `matched` drives the pager
// and the tab counts; `visible` additionally holds parent rows kept only so a
// matching child is not left dangling under a heading it does not belong to.
export interface FilterResult {
    matched: Set<AppId>
    visible: Set<AppId>
    visibleGroups: Set<AppId>
}

export function filterRequirementType(
    type: UrsRequirementType,
    filter: RequirementFilter,
    index: FilterIndex,
): FilterResult {
    const matched = new Set<AppId>()
    const visible = new Set<AppId>()
    const visibleGroups = new Set<AppId>()

    const walk = (req: URSRequirement, groupId: AppId | null): boolean => {
        const self = matchesFilter(req, filter, index)
        if (self) matched.add(req.id)

        let anyChild = false
        for (const child of req.childRequirements) {
            // A child never pulls in its siblings, only its ancestors.
            if (walk(child, groupId)) anyChild = true
        }

        const show = self || anyChild
        if (show) {
            visible.add(req.id)
            if (groupId !== null) visibleGroups.add(groupId)
        }
        return show
    }

    for (const req of type.requirements) {
        walk(req, null)
    }
    for (const group of type.groups) {
        for (const req of group.requirements) {
            walk(req, group.id)
        }
    }

    return { matched, visible, visibleGroups }
}

// Document-order list of every requirement that survives the filter, paired with
// its type so the pager can switch the tab behind the dialog.
//
// Deliberately mirrors allURSRequirementsFlat exactly, including its single level
// of child requirements: the pager resolves these ids back against that generator,
// so anything yielded here that it does not yield would silently drop out of the
// navigation. Both need changing together if requirements ever nest deeper.
export function filteredRequirementsFlat(
    urs: Urs,
    filter: RequirementFilter,
    index: FilterIndex,
): [URSRequirement, UrsRequirementType][] {
    const out: [URSRequirement, UrsRequirementType][] = []
    const push = (req: URSRequirement, type: UrsRequirementType) => {
        if (matchesFilter(req, filter, index)) out.push([req, type])
        for (const child of req.childRequirements) {
            if (matchesFilter(child, filter, index)) out.push([child, type])
        }
    }
    for (const type of urs.types) {
        for (const req of type.requirements) {
            push(req, type)
        }
        for (const group of type.groups) {
            for (const req of group.requirements) {
                push(req, type)
            }
        }
    }
    return out
}
